/**
 * What to do with a Supabase auth event.
 *
 * This is a pure function in a lib rather than a branch inside App's listener
 * for the reason test/hub-render.test.tsx gives: there is no jsdom in this
 * project, so anything worth testing has to be callable directly. And this is
 * worth testing — it is the whole of OPS-28, and the shape of the bug means a
 * regression is invisible in code review and obvious to every user.
 *
 * THE BUG IT FIXES
 *
 * `onAuthStateChange` is not an "identity changed" event. supabase-js
 * registers its own `visibilitychange` handler and calls `_recoverAndRefresh()`
 * whenever the tab is fronted, which emits TOKEN_REFRESHED / SIGNED_IN
 * carrying the SAME user. App used to blank to the splash and re-resolve on
 * every event, so fronting the tab unmounted the tree, re-ran loadMe (three
 * queries in two sequential waves) and — because Home unmounted with it —
 * refetched the feed, the wiki answers and the rebrand board as well.
 *
 * So the question to ask an auth event is "is this a different person than the
 * one on screen", never "did an event happen".
 */

/** The auth user id App has already resolved a Me for. */
export type Resolved =
  /** Nothing resolved yet — a cold start. */
  | undefined
  /** Resolved as signed out. */
  | null
  /** Resolved as this auth user. */
  | string

export type AuthTransition =
  /** Same person as the one on screen. A token refresh or a tab re-focus. */
  | { kind: 'ignore' }
  | { kind: 'signedOut' }
  | { kind: 'resolve'; authUserId: string }

export function authTransition(
  resolved: Resolved,
  incomingAuthUserId: string | null,
): AuthTransition {
  // The early return that is the entire fix. Note it also covers the
  // getSession()/INITIAL_SESSION pair that both fire on mount: before this,
  // every cold load ran loadMe twice concurrently.
  if (resolved !== undefined && incomingAuthUserId === resolved) {
    return { kind: 'ignore' }
  }
  if (incomingAuthUserId === null) return { kind: 'signedOut' }
  return { kind: 'resolve', authUserId: incomingAuthUserId }
}

/**
 * Whether to clear the screen while resolving.
 *
 * Always, on the resolve path — and that is worth stating rather than leaving
 * implicit, because the first cut of this fix showed the splash only on a cold
 * start and kept the current UI otherwise. That looks like the considerate
 * choice and is wrong: the only ways to reach `resolve` are a cold start
 * (nothing on screen), a sign-in from the SignIn screen, or a switch to a
 * DIFFERENT person — and in that last case what is on screen is somebody
 * else's name, tenant, roles and feed. Leaving it up for the length of three
 * queries renders one colleague's hub under another colleague's session.
 *
 * A function rather than a literal `true` so the reasoning has somewhere to
 * live and the test has something to assert against.
 */
export function shouldClearWhileResolving(): boolean {
  return true
}
